import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useBannerManagement, type BannerFormData } from "@/admin/hooks/useBannerManagement";
import { useBannerImageUpload } from "@/admin/hooks/useBannerImageUpload";

const PRESETS = [
  { label: "Orange Hirafi", bg: "#E0561A", text: "#FFFFFF" },
  { label: "Vert vérifié", bg: "#317A55", text: "#FFFFFF" },
  { label: "Ambre chaud", bg: "#F59E0B", text: "#FFFFFF" },
  { label: "Crème (discret)", bg: "#F9F6F2", text: "#251809" },
  { label: "Foncé", bg: "#1C1917", text: "#FFFFFF" },
];

const schema = z.object({
  title_fr: z.string().min(1, "Requis"),
  title_ar: z.string().min(1, "Requis"),
  subtitle_fr: z.string().optional(),
  subtitle_ar: z.string().optional(),
  image_url: z.string().optional(),
  image_storage_path: z.string().optional(),
  image_alt_fr: z.string().optional(),
  image_alt_ar: z.string().optional(),
  background_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Format hex requis (ex: #E0561A)"),
  text_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Format hex requis (ex: #FFFFFF)"),
  cta_url: z.string().url("URL invalide").optional().or(z.literal("")),
  cta_text_fr: z.string().optional(),
  cta_text_ar: z.string().optional(),
  cta_target: z.enum(["_self", "_blank"]),
  is_active: z.boolean(),
  dismissible: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface BannerFormProps {
  bannerId?: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export function BannerForm({ bannerId, onSuccess, onCancel }: BannerFormProps) {
  const { query, upsertMutation } = useBannerManagement();
  const uploadMutation = useBannerImageUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title_fr: "",
      title_ar: "",
      background_color: "#E0561A",
      text_color: "#FFFFFF",
      cta_target: "_self",
      is_active: false,
      dismissible: true,
    },
  });

  // Pré-remplir si édition
  useEffect(() => {
    if (!bannerId || !query.data) return;
    const banner = query.data.find((b) => b.id === bannerId);
    if (!banner) return;
    form.reset({
      title_fr: banner.title_fr ?? "",
      title_ar: banner.title_ar ?? "",
      subtitle_fr: banner.subtitle_fr ?? "",
      subtitle_ar: banner.subtitle_ar ?? "",
      image_url: banner.image_url ?? "",
      image_storage_path: banner.image_storage_path ?? "",
      image_alt_fr: banner.image_alt_fr ?? "",
      image_alt_ar: banner.image_alt_ar ?? "",
      background_color: banner.background_color ?? "#E0561A",
      text_color: banner.text_color ?? "#FFFFFF",
      cta_url: banner.cta_url ?? "",
      cta_text_fr: banner.cta_text_fr ?? "",
      cta_text_ar: banner.cta_text_ar ?? "",
      cta_target: (banner.cta_target as "_self" | "_blank") ?? "_self",
      is_active: banner.is_active ?? false,
      dismissible: banner.dismissible ?? true,
    });
  }, [bannerId, query.data, form]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadMutation.mutateAsync(file);
    form.setValue("image_url", result.publicUrl);
    form.setValue("image_storage_path", result.storagePath);
  };

  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    form.setValue("background_color", preset.bg);
    form.setValue("text_color", preset.text);
  };

  const onSubmit = (values: FormValues) => {
    upsertMutation.mutate(
      { id: bannerId ?? undefined, formData: values as BannerFormData },
      { onSuccess }
    );
  };

  const imageUrl = form.watch("image_url");
  const bgColor = form.watch("background_color");
  const textColor = form.watch("text_color");
  const titleFr = form.watch("title_fr");

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 max-h-[75vh] overflow-y-auto pe-1">

      {/* Aperçu en direct */}
      <div
        className="rounded-lg px-4 py-3 flex items-center gap-3 text-sm font-semibold transition-all"
        style={{ backgroundColor: bgColor, color: textColor }}
      >
        {imageUrl && (
          <img src={imageUrl} alt="" className="h-7 w-7 object-contain flex-shrink-0" />
        )}
        <span>{titleFr || "Aperçu du bandeau…"}</span>
      </div>

      {/* Titres FR / AR */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Titre FR *</Label>
          <Input {...form.register("title_fr")} placeholder="Ramadan Kareem" />
          {form.formState.errors.title_fr && (
            <p className="text-xs text-destructive">{form.formState.errors.title_fr.message}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label>Titre AR *</Label>
          <Input {...form.register("title_ar")} dir="rtl" placeholder="رمضان كريم" />
          {form.formState.errors.title_ar && (
            <p className="text-xs text-destructive">{form.formState.errors.title_ar.message}</p>
          )}
        </div>
      </div>

      {/* Sous-titres */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Sous-titre FR</Label>
          <Textarea {...form.register("subtitle_fr")} rows={2} placeholder="Description optionnelle…" />
        </div>
        <div className="space-y-1">
          <Label>Sous-titre AR</Label>
          <Textarea {...form.register("subtitle_ar")} dir="rtl" rows={2} />
        </div>
      </div>

      {/* Image */}
      <div className="space-y-2 border-t pt-4">
        <Label>Image / Logo (optionnel)</Label>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="gap-2"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {imageUrl ? "Changer l'image" : "Uploader une image"}
          </Button>
          {imageUrl && (
            <img src={imageUrl} alt="Aperçu" className="h-10 w-10 rounded object-contain border" />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
        </div>
        {imageUrl && (
          <div className="grid grid-cols-2 gap-2">
            <Input {...form.register("image_alt_fr")} placeholder="Alt FR (accessibilité)" />
            <Input {...form.register("image_alt_ar")} dir="rtl" placeholder="Alt AR" />
          </div>
        )}
      </div>

      {/* Couleurs */}
      <div className="space-y-2 border-t pt-4">
        <Label>Couleurs</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p)}
              className="rounded-md px-2.5 py-1 text-xs font-medium border transition-opacity hover:opacity-80"
              style={{ backgroundColor: p.bg, color: p.text }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Fond</Label>
            <div className="flex items-center gap-2">
              <input type="color" {...form.register("background_color")} className="h-9 w-12 cursor-pointer rounded border p-0.5" />
              <Input {...form.register("background_color")} className="font-mono text-sm" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Texte</Label>
            <div className="flex items-center gap-2">
              <input type="color" {...form.register("text_color")} className="h-9 w-12 cursor-pointer rounded border p-0.5" />
              <Input {...form.register("text_color")} className="font-mono text-sm" />
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="space-y-3 border-t pt-4">
        <Label>Appel à l'action (CTA) — optionnel</Label>
        <Input {...form.register("cta_url")} type="url" placeholder="https://…" />
        <div className="grid grid-cols-2 gap-2">
          <Input {...form.register("cta_text_fr")} placeholder="Texte bouton FR" />
          <Input {...form.register("cta_text_ar")} dir="rtl" placeholder="Texte bouton AR" />
        </div>
        <Select
          value={form.watch("cta_target")}
          onValueChange={(v) => form.setValue("cta_target", v as "_self" | "_blank")}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_self">Même onglet</SelectItem>
            <SelectItem value="_blank">Nouvel onglet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Options */}
      <div className="space-y-3 border-t pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Actif</p>
            <p className="text-xs text-muted-foreground">Rend le bandeau visible sur le site</p>
          </div>
          <Switch
            checked={form.watch("is_active")}
            onCheckedChange={(v) => form.setValue("is_active", v)}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Dismissible</p>
            <p className="text-xs text-muted-foreground">L'utilisateur peut fermer le bandeau</p>
          </div>
          <Switch
            checked={form.watch("dismissible")}
            onCheckedChange={(v) => form.setValue("dismissible", v)}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={upsertMutation.isPending || uploadMutation.isPending}>
          {upsertMutation.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
          {bannerId ? "Enregistrer" : "Créer le bandeau"}
        </Button>
      </div>
    </form>
  );
}
